// SEOHead.jsx - Dynamic SEO Meta Tags Component
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

const FALLBACK_SITE_URL = process.env.REACT_APP_SITE_URL || 'https://bookatherapy.com';

function buildAbsoluteUrl(urlLike, fallback = FALLBACK_SITE_URL) {
  if (!urlLike) {
    if (typeof window !== 'undefined') {
      const { origin, pathname, search } = window.location;
      return `${origin}${pathname}${search}` || fallback;
    }
    return fallback;
  }

  try {
    return new URL(urlLike).href;
  } catch {
    try {
      return new URL(urlLike.replace(/^\//, ''), fallback.endsWith('/') ? fallback : `${fallback}/`).href;
    } catch {
      return fallback;
    }
  }
}

function normalizeCanonical(url) {
  const absolute = buildAbsoluteUrl(url);
  if (!absolute) return undefined;

  try {
    const normalized = new URL(absolute);
    if (normalized.pathname !== '/' && normalized.pathname.endsWith('/')) {
      normalized.pathname = normalized.pathname.replace(/\/+$/, '');
    }
    const serialized = normalized.toString();
    return normalized.pathname === '/' ? serialized.replace(/\/$/, '') : serialized;
  } catch {
    return absolute.endsWith('/') && absolute !== `${FALLBACK_SITE_URL}/`
      ? absolute.slice(0, -1)
      : absolute;
  }
}

/**
 * SEO Head Component
 * Dynamically manages meta tags, structured data, and SEO elements
 */
export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noindex = false,
  nofollow = false,
  schema,
  structuredData,
  disableCanonical = false,
  children
}) {
  // Default values
  const fullTitle = title ? `${title} | BookaTherapy` : 'BookaTherapy - Book Professional Massage Therapy in Canada';
  const finalDescription = description || 'Find and book professional massage therapy with certified RMTs. Home, mobile, or clinic appointments across Canada. Transparent pricing, personalized matching.';
  const finalCanonical = useMemo(() => normalizeCanonical(canonical), [canonical]);
  const finalOgImage = buildAbsoluteUrl(ogImage || 'https://bookatherapy.com/og-image.jpg');
  const robots = (noindex ? 'noindex' : 'index') + ',' + (nofollow ? 'nofollow' : 'follow');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={finalDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {!disableCanonical && finalCanonical && <link rel="canonical" href={finalCanonical} />}
      <meta name="robots" content={robots} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {finalCanonical && <meta property="og:url" content={finalCanonical} />}
      <meta property="og:title" content={ogTitle || fullTitle} />
      <meta property="og:description" content={ogDescription || finalDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:site_name" content="BookaTherapy" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      {finalCanonical && <meta property="twitter:url" content={finalCanonical} />}
      <meta property="twitter:title" content={ogTitle || fullTitle} />
      <meta property="twitter:description" content={ogDescription || finalDescription} />
      <meta property="twitter:image" content={finalOgImage} />

      {/* Additional SEO Tags */}
      <meta name="author" content="BookaTherapy" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="CA" />
      <meta name="geo.placename" content="Canada" />

      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />

      {/* Structured Data (JSON-LD) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}

      {/* Multiple Structured Data (JSON-LD) */}
      {structuredData && Array.isArray(structuredData) && structuredData.map((data, index) => (
        data && (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        )
      ))}

      {/* Custom head elements */}
      {children}
    </Helmet>
  );
}
